<template>
  <section class="reading-panel" aria-label="Okuma metni">
    <div class="reading-book">
      <img src="../../../assets/images/book/book-title-logo.png" alt="" class="book-title-logo">
      <div class="book-titles">
        <span class="book-title">{{ BookParentTitle }}</span>
        <span class="book-title-2">{{ BookSubTitle }}</span>
        <span class="book-sub-title">Reading</span>
      </div>
    </div>
    <SubjectBookImage v-if="readingImageUrl" :url="readingImageUrl" banner alt="Okuma bölümü görseli" />
    <div v-if="hasAudio || hasTranslation" class="reading-toolbar">
      <template v-if="hasAudio">
        <button type="button" class="reading-button reading-play" :disabled="AudioDisabled" @click="audioIsStatus === 'play' ? pauseAudio() : playAudio()">
          <i :class="audioIsStatus === 'play' ? 'bi bi-pause-fill' : 'bi bi-play-fill'" aria-hidden="true"></i>
          {{ audioIsStatus === 'play' ? 'Duraklat' : 'Dinle' }}
        </button>
        <button type="button" class="reading-button" :disabled="AudioDisabled || audioIsStatus === 'stop'" @click="stopAudio()">
          <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i> Başa dön
        </button>
        <label class="reading-speed">Dinleme hızı
          <select :value="audioSpeed" :disabled="AudioDisabled" @change="ChangeAudioSpeed(Number($event.target.value))">
            <option :value="0.75">0,75×</option><option :value="1">Normal</option><option :value="1.25">1,25×</option>
          </select>
        </label>
      </template>
      <ContentLanguageSwitch v-if="hasTranslation" :language="contentLanguage" @language="selectLanguage" />
    </div>
    <p v-if="hasAudio && AudioDisabled" class="reading-hint">Sesli okumayı dinlemek için EN dilini seçin.</p>
    <p v-if="audioError" class="reading-error" role="status">{{ audioError }}</p>
    <div v-if="bookContent.contentHtml && bookContent.contentHtml.trim()" class="reading-text" :lang="contentLanguage === 'en-EN' ? 'en' : 'tr'" v-html="bookContent.contentHtml"></div>
    <p v-else class="reading-empty" role="status">Bu bölüm için henüz okuma metni eklenmemiş.</p>
    <audio ref="ReadPlay" :src="audioSrc || undefined" preload="none" @ended="onAudioEnded" @error="onAudioError"></audio>
  </section>
</template>

<script>
import '../../../assets/styles/book-design.css';
import ContentLanguageSwitch from './ContentLanguageSwitch.vue';
import SubjectBookImage from './SubjectBookImage.vue';
export default {
  name: 'TabLearnReading',
  components: { SubjectBookImage, ContentLanguageSwitch },
  emits: ['language-availability', 'language'],
  props: {
    language: { type: String, default: 'en-EN' },
    externalLanguageControl: { type: Boolean, default: false },
    contents: { type: Array, default: () => [] },
    bookTitle: { type: Object, default: () => ({}) }
  },
  data() {
    return { contentLanguage: 'en-EN', audioSpeed: 1, audioIsStatus: 'stop', audioError: '', audioRequest: 0 };
  },
  computed: {
    readingSource() { return (this.contents || []).find(item => item.contentType === 13); },
    readingImageUrl() {
      const fileName = this.readingSource?.sectionImageFileName?.trim();
      return fileName ? process.env.VUE_APP_ROOT_API + 'Book/SectionImage?fileName=' + encodeURIComponent(fileName) : '';
    },
    hasTranslation() {
      return !!this.readingSource?.languages?.some(item => item.languageCode === 'tr-TR' && item.contentHtml?.trim());
    },
    bookContent() {
      const source = this.readingSource;
      const translation = source?.languages?.find(item => item.languageCode === 'tr-TR');
      return { contentHtml: (this.contentLanguage === 'en-EN' ? source?.contentHtml : translation?.contentHtml) || '' };
    },
    hasAudio() { return !!this.readingSource?.contentAudioFile?.trim(); },
    audioSrc() {
      return this.hasAudio ? process.env.VUE_APP_ROOT_API + 'Source/dictionary/bookreadaudio?fileName=' + encodeURIComponent(this.readingSource.contentAudioFile) : '';
    },
    AudioDisabled() { return this.contentLanguage !== 'en-EN' || !this.hasAudio; },
    BookParentTitle() { return this.bookTitle?.bookParentTitle || ''; },
    BookSubTitle() { return this.bookTitle?.bookSubTitle || ''; }
  },
  methods: {
    applyLanguage() {
      const next = this.hasTranslation ? this.language : 'en-EN';
      if (next === this.contentLanguage) return;
      this.stopAudio();
      this.contentLanguage = next;
      this.audioError = '';
    },
    selectLanguage(language) {
      if (this.externalLanguageControl) { this.$emit('language', language); return; }
      if (language !== this.contentLanguage) this.ChangeLanguage();
    },
    ChangeLanguage() {
      this.stopAudio();
      this.contentLanguage = this.contentLanguage === 'en-EN' ? 'tr-TR' : 'en-EN';
      this.audioError = '';
    },
    ChangeAudioSpeed(value) {
      this.audioSpeed = value;
      if (this.$refs.ReadPlay) this.$refs.ReadPlay.playbackRate = value;
    },
    async playAudio() {
      if (this.AudioDisabled || !this.$refs.ReadPlay) return;
      const request = ++this.audioRequest;
      this.audioError = '';
      try {
        this.$refs.ReadPlay.playbackRate = this.audioSpeed;
        await this.$refs.ReadPlay.play();
        if (request === this.audioRequest) this.audioIsStatus = 'play';
      } catch (error) {
        if (request !== this.audioRequest || error?.name === 'AbortError') return;
        this.onAudioError();
      }
    },
    pauseAudio() {
      this.audioRequest++;
      this.$refs.ReadPlay?.pause();
      this.audioIsStatus = 'pause';
    },
    stopAudio() {
      this.audioRequest++;
      this.$refs.ReadPlay?.pause();
      if (this.$refs.ReadPlay) this.$refs.ReadPlay.currentTime = 0;
      this.audioIsStatus = 'stop';
    },
    onAudioEnded() { this.audioIsStatus = 'stop'; },
    onAudioError() {
      if (!this.hasAudio) return;
      this.audioIsStatus = 'stop';
      this.audioError = 'Ses açılamadı. Lütfen yeniden deneyin.';
    }
  },
  beforeUnmount() { this.stopAudio(); },
  watch: {
    hasTranslation: { immediate: true, handler(value) {
      this.$emit('language-availability', value);
      this.applyLanguage();
    } },
    language() { this.applyLanguage(); },
    readingSource() { this.stopAudio(); },
    audioSrc() { this.stopAudio(); this.audioError = ''; }
  }
};
</script>

<style scoped>
.reading-panel { width: 100%; min-width: 0; }
.reading-book { display: flow-root; margin-bottom: 24px; }
.reading-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding-bottom: 20px; margin-bottom: 24px; border-bottom: 1px solid #eef2f6; }
.reading-button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 40px; border: 1px solid #e2e8f0; border-radius: 10px; background: white; color: #526174; padding: 8px 12px; font-size: 14px; font-weight: 600; }
.reading-button:hover:not(:disabled) { background: #f1f5f9; }
.reading-play { background: var(--brand-primary); border-color: var(--brand-primary); color: white; }
.reading-play:hover:not(:disabled) { background: var(--brand-primary-hover); border-color: var(--brand-primary-hover); }
.reading-speed { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; color: #526174; font-size: 13px; }
.reading-speed select { min-height: 40px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 6px 8px; background: white; color: #20382d; }
.reading-button:disabled, .reading-speed select:disabled { opacity: .5; cursor: not-allowed; }
.reading-button:focus-visible, .reading-speed select:focus-visible { outline: 3px solid var(--brand-primary-hover); outline-offset: 3px; }
.reading-text { color: #334155; font-size: 18px; line-height: 1.8; overflow-wrap: anywhere; }
.reading-text :deep(p) { margin-bottom: 1.25em; }
.reading-text :deep(h1), .reading-text :deep(h2), .reading-text :deep(h3) { color: #20382d; line-height: 1.4; margin: 1.5em 0 .75em; }
.reading-text :deep(img) { max-width: 100%; height: auto; }
.reading-text :deep(table) { display: block; max-width: 100%; overflow-x: auto; }
.reading-hint, .reading-empty { color: #64748b; font-size: 14px; line-height: 1.6; }
.reading-empty { padding: 24px 0; }
.reading-error { color: #b45309; background: #fffbeb; border-radius: 8px; padding: 10px 12px; font-size: 14px; }
@media (max-width: 575px) {
  .reading-text { font-size: 17px; }
  .reading-toolbar { gap: 8px; margin-bottom: 20px; }
  .reading-button { padding: 8px 10px; }
  .reading-speed { margin-left: 0; }
}
</style>
